import type { FC } from "react"
import React, { useEffect, useState } from "react"
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Tag,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import invariant from "tiny-invariant"

import type { FixedSafeMultisigTransactionResponse } from "safe/hooks/useSafeTransactionV2"
import { getChainIdParams } from "web3/helpers/chainId"
import Web3Button from "web3/components/Web3Button"
import ErrorBoundary from "common/components/ErrorBoundary"
import { ROUTES } from "common/constants/routes"
import type {
  Governor,
  OnchainVote,
  Proposal,
  ProposalActionAttemptQuery,
  ProposalActionAttemptQueryVariables,
  ProposalActiveCtaQuery,
  ProposalVoteAttemptQuery,
  ProposalVoteAttemptQueryVariables,
  Vote,
} from "query/graphql"
import {
  ProposalStatus,
  ProposalEventType,
  ProposalVoteAttemptDocument,
  useProposalActiveCtaQuery,
  useDelegateStatementQuery,
  ProposalActionAttemptDocument,
  ProposalActionType,
  VoteType,
} from "query/graphql"
import ProposalShareMenuItem from "proposal/components/ctas/ProposalShareMenuItem"
import ProposalBlockExplorerURLButton from "proposal/components/ctas/ProposalBlockExplorerURLButton"
import ProposalCancelButton from "proposal/components/ctas/ProposalCancelButton"
import { getGovernorProposalTxHash } from "proposal/helpers/status"
import { useQuery } from "query/hooks/useQuery"
import { EllipsisVertical } from "ui/components/icons/font-awesome/EllipsisVertical"
import { GrpcErrorType } from "common/helpers/fetcher"
import { getVoteStyles } from "common/helpers/voteStyle"
import { useModals } from "common/hooks/useModals"
import { Repeat } from "ui/components/icons/font-awesome/Repeat"
import { useMe } from "user/providers/MeProvider"
import { useChainReference } from "web3/hooks/useChainReference"
import { useSafeTransactionV2 } from "safe/hooks/useSafeTransactionV2"
import ShareToFarcasterButton from "common/components/ShareToFarcasterButton"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { useDescriptionException } from "proposal/hooks/useDescriptionException"
import { FeatureFlag, useGovernanceContext } from "governance/context"
import ShareToLensButton from "common/components/ShareToLensButton"
import { isMultiChainTokenIds } from "organization/helpers/organization"
import OrganizationDisclaimerModal from "organization/components/disclaimer/OrganizationDisclaimerModal"
import type { OrganizationDisclaimerOptions } from "organization/constants/org-disclamer"
import { getOrganizationDisclaimer } from "organization/constants/org-disclamer"
import { useDescriptionExceptions } from "proposal/hooks/useDescriptionExceptions"
import { _getDescriptionException } from "proposal/helpers/description"
import StakeAccountIntercept from "web3/solana/wormhole-staking/StakeAccountIntercept"
import { useAddress } from "web3/hooks/useAddress"

type ProposalActiveCTAProps = {
  proposalId: string
  slug: string
}
const ProposalActiveCTA: FC<ProposalActiveCTAProps> = ({
  proposalId,
  slug,
}) => {
  const { organization } = useOrganization()
  const chainReference = useChainReference()
  const { voteModal, voteOnboarding } = useModals()
  const me = useMe()
  const [isRetryVote, setIsRetryVote] = useState(false)
  const [safeTx, setSafeTx] = useState<
    FixedSafeMultisigTransactionResponse | undefined
  >(undefined)
  const [pendingTransactions, setPendingTransactions] = useState<
    FixedSafeMultisigTransactionResponse[] | undefined
  >(undefined)

  const [orgDisclaimer, setOrgDisclaimer] =
    useState<OrganizationDisclaimerOptions | null>(null)

  const isMultiChain = isMultiChainTokenIds(organization?.tokenIds)
  const { descriptionExceptions } = useDescriptionExceptions()

  const {
    isOpen: isVoteDisclaimerModalOpen,
    onOpen: onVoteDisclaimerModalOpen,
    onClose: onVoteDisclaimerModalClose,
  } = useDisclosure()
  const stakeAccountDisclosure = useDisclosure()

  useEffect(() => {
    const _orgDisclaimer = organization
      ? getOrganizationDisclaimer(organization.id)
      : undefined

    if (_orgDisclaimer) {
      setOrgDisclaimer(_orgDisclaimer)

      return
    }
  }, [organization])

  const voterAddress = useAddress()

  const hasOutstandingVotes = (data?: ProposalActiveCtaQuery): boolean => {
    if (!organization?.chainIds) {
      return false
    }

    const votes = data?.votes

    const pendingVotes = votes
      ? (votes.nodes as Vote[])
          .filter((vote): vote is OnchainVote => "chainId" in vote)
          .filter((vote) => {
            const { type } = vote

            // TODO(@nicolas): Temporary fix since proposal.voteStats.type has a different value than VoteType
            // For example: "pending_for" vs "pendingfor" (from VoteType)
            return (
              type === VoteType.Pendingabstain ||
              type.replace("_", "") === VoteType.Pendingabstain ||
              type === VoteType.Pendingagainst ||
              type.replace("_", "") === VoteType.Pendingagainst ||
              type === VoteType.Pendingfor ||
              type.replace("_", "") === VoteType.Pendingfor
            )
          })
      : []

    return pendingVotes.length > 0 && isMultiChain
  }

  const { data, isLoading } = useProposalActiveCtaQuery(
    {
      input: { id: proposalId },
      votersInput: {
        filters: {
          voter: Boolean(voterAddress)
            ? me?.type === "SAFE"
              ? me.address
              : voterAddress
            : undefined,
          proposalId,
          includePendingVotes: isMultiChain,
        },
      },
    },
    {
      refetchInterval: (data) => {
        const vote =
          Boolean(voterAddress) && data?.votes
            ? (data.votes.nodes[0] as Vote)
            : null

        return vote && !hasOutstandingVotes(data) ? false : 30000
      },
    },
  )

  const { data: voteAttemptData } = useQuery<
    ProposalVoteAttemptQuery,
    ProposalVoteAttemptQueryVariables
  >(
    {
      query: ProposalVoteAttemptDocument,
      variables: {
        input: {
          voter: voterAddress ?? "",
          proposalId,
        },
      },
      omittedErrors: [GrpcErrorType.NotFound],
    },
    {
      enabled: Boolean(voterAddress) && voteModal.isOpen === false,
    },
  )

  const { getSafeTransaction, getPendingTransactions } = useSafeTransactionV2(
    {
      safeId: me?.id as string,
      accountAddress: me?.address,
    },
    { enabled: me?.type === "SAFE" },
  )

  const pendingSafeTx = pendingTransactions
    ? pendingTransactions.find((pendingTx) => {
        if (pendingTx.safeTxHash === safeTx?.safeTxHash) {
          return pendingTx
        }
      })
    : undefined

  const isRejectedSafeTx = Boolean(!pendingSafeTx) && me?.type === "SAFE"

  const { data: delegateStatementData, refetch: delegateProfileRefetch } =
    useDelegateStatementQuery(
      {
        input: {
          address: voterAddress ?? "",
          organizationId: organization ? organization.id : "",
        },
      },
      {
        enabled:
          Boolean(voterAddress) &&
          Boolean(organization) &&
          Boolean(orgDisclaimer),
        refetchOnWindowFocus: true,
      },
    )

  const delegateProfile =
    delegateStatementData?.delegate?.statement ?? undefined
  const hideDisclaimer = delegateProfile?.hideDisclaimer ?? false
  const isMember = delegateProfile?.isMember ?? false

  useEffect(() => {
    if (me?.type === "SAFE" && voteAttemptData?.voteAttempt?.txHash) {
      getSafeTransaction(voteAttemptData.voteAttempt.txHash).then((safeTx) => {
        setSafeTx(safeTx)
      })

      getPendingTransactions().then((results) => {
        setPendingTransactions(results)
      })
    }
  }, [
    getSafeTransaction,
    getPendingTransactions,
    me?.address,
    me?.type,
    voteAttemptData?.voteAttempt?.txHash,
  ])

  const internalProposalId = data?.proposal?.id ?? undefined

  const { data: proposalActionAttemptData } = useQuery<
    ProposalActionAttemptQuery,
    ProposalActionAttemptQueryVariables
  >(
    {
      query: ProposalActionAttemptDocument,
      variables: {
        input: {
          proposalId: internalProposalId ?? "",
          type: ProposalActionType.Cancel,
        },
      },
      omittedErrors: [GrpcErrorType.NotFound],
    },
    { enabled: internalProposalId !== undefined },
  )

  if (isLoading) return null

  if (!data) return null

  const { proposal, votes } = data

  invariant(proposal.governor, "governor is required")

  const { chainId } = proposal.governor

  const handleVoteClick = (): void => {
    const { reference: governanceReference } = getChainIdParams(chainId)

    const shouldShowOnboarding =
      !voterAddress ||
      proposal.status === ProposalStatus.Pending ||
      chainReference !== governanceReference

    if (shouldShowOnboarding) {
      voteOnboarding.onOpen()

      return
    }

    // If vote disclaimer exists and the user is not a member
    // or didn't select to hide it, then display modal
    if (orgDisclaimer && voterAddress && (!isMember || !hideDisclaimer)) {
      onVoteDisclaimerModalOpen()

      return
    }

    voteModal.onOpen()
  }

  const vote = Boolean(voterAddress) && votes ? (votes.nodes[0] as Vote) : null

  // Check out if voting option should be hidden
  const { descriptionException } = _getDescriptionException({
    descriptionExceptions,
    governanceId: proposal.governor.id ?? "",
    proposalId: proposal.id,
    proposalOnchainId: proposal.onchainId ?? "",
  })

  const hideVoteButton = descriptionException?.hideVoteButton ?? false

  const hasPendingVotes = hasOutstandingVotes(data)

  if (proposalActionAttemptData) {
    return (
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={4}
        width="100%"
      >
        <Stack
          align="center"
          direction="row"
          spacing={3}
          width={{ base: "100%", sm: "auto" }}
        >
          <Spinner />
          <Text textStyle="body.bold.lg">Canceling Proposal</Text>
        </Stack>
        <OptionsList
          governance={proposal.governor as Governor}
          proposal={proposal as Proposal}
          slug={slug}
        />
      </Stack>
    )
  }

  if (vote && !hasOutstandingVotes(data)) {
    const support = vote.type

    const voteStyles = getVoteStyles()
    const { color } = voteStyles[support]

    return (
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={4}
        width="100%"
      >
        <Stack
          align="center"
          data-qa="proposal-details-voting-status"
          direction="row"
          width={{ base: "100%", sm: "auto" }}
        >
          <Text align="center" color="gray.600" textStyle="body.bold.lg">
            You voted
          </Text>
          <Tag bg={color} color="white" size="md">
            {support}
          </Tag>
        </Stack>
        <OptionsList
          governance={proposal.governor as Governor}
          proposal={proposal as Proposal}
          slug={slug}
        />
      </Stack>
    )
  }

  if (voteAttemptData?.voteAttempt && !isRetryVote) {
    const { voteAttempt } = voteAttemptData

    if (
      (isRejectedSafeTx && !Boolean(pendingTransactions)) ||
      hasOutstandingVotes(data)
    ) {
      return (
        <ErrorBoundary section="ProposalVoteButton">
          <Stack
            align="center"
            color="gray.600"
            direction="row"
            justify={{ base: "center", lg: "right" }}
            spacing={4}
            width="100%"
          >
            {hasPendingVotes ? (
              <Text align="center" color="gray.600" textStyle="body.bold.lg">
                Your vote is pending to bridge
              </Text>
            ) : null}

            {hideVoteButton ? (
              <Text textStyle="body.bold.lg">Voting is not available</Text>
            ) : null}

            {!hasPendingVotes && !hideVoteButton ? (
              <Web3Button
                px={6}
                requiredChainId={chainId}
                width={{ base: "100%", sm: "auto" }}
                onClick={isMultiChain ? voteModal.onOpen : undefined}
                onValid={handleVoteClick}
              >
                Vote onchain
              </Web3Button>
            ) : null}
            <OptionsList
              governance={proposal.governor as Governor}
              proposal={proposal as Proposal}
              slug={slug}
            />
          </Stack>
          {orgDisclaimer && organization ? (
            <OrganizationDisclaimerModal
              delegateProfile={delegateProfile}
              delegateProfileRefetch={delegateProfileRefetch}
              isDisclaimerModalOpen={isVoteDisclaimerModalOpen}
              modalContent={orgDisclaimer.vote.content}
              organization={organization}
              onDisclaimerModalClose={onVoteDisclaimerModalClose}
              onSuccess={() => voteModal.onOpen()}
            />
          ) : null}
        </ErrorBoundary>
      )
    }

    return (
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={4}
        width="100%"
      >
        <Stack
          align="center"
          data-qa="proposal-details-vote-onchain-status"
          direction="row"
          spacing={3}
          width={{ base: "100%", sm: "auto" }}
        >
          <Spinner />
          <Text textStyle="body.bold.lg">
            {me?.type === "SAFE" ? "Waiting for signers" : "Submitting vote"}
          </Text>
        </Stack>

        <OptionsList
          governance={proposal.governor as Governor}
          proposal={proposal as Proposal}
          slug={slug}
          voteAttempt={voteAttempt}
          onRetryVote={() => setIsRetryVote(true)}
        />
      </Stack>
    )
  }

  return (
    <ErrorBoundary section="ProposalVoteButton">
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={4}
        width="100%"
      >
        {hasPendingVotes ? (
          <Text align="center" color="gray.600" textStyle="body.bold.lg">
            Your vote is pending to bridge
          </Text>
        ) : null}

        {hideVoteButton ? (
          <Text textStyle="body.bold.lg">Voting is not available</Text>
        ) : null}

        {!hasPendingVotes && !hideVoteButton ? (
          <Web3Button
            data-qa="proposal-details-vote-onchain-btn"
            px={6}
            requiredChainId={chainId}
            width={{ base: "100%", sm: "auto" }}
            onClick={isMultiChain ? voteModal.onOpen : undefined}
            onValid={handleVoteClick}
          >
            Vote onchain
          </Web3Button>
        ) : null}
        <OptionsList
          governance={proposal.governor as Governor}
          proposal={proposal as Proposal}
          slug={slug}
        />
      </Stack>
      {orgDisclaimer && organization ? (
        <OrganizationDisclaimerModal
          delegateProfile={delegateProfile}
          delegateProfileRefetch={delegateProfileRefetch}
          isDisclaimerModalOpen={isVoteDisclaimerModalOpen}
          modalContent={orgDisclaimer.vote.content}
          organization={organization}
          onDisclaimerModalClose={onVoteDisclaimerModalClose}
          onSuccess={() => voteModal.onOpen()}
        />
      ) : null}
      {/* TODO(@nicolas): Coderabbit found that stakeAccountDisclosure.onOpen() is never triggered */}
      <StakeAccountIntercept
        voting
        isOpen={stakeAccountDisclosure.isOpen}
        onClose={stakeAccountDisclosure.onClose}
      />
    </ErrorBoundary>
  )
}

type OptionsListProps = {
  governance: Governor
  proposal: Proposal
  voteAttempt?: ProposalVoteAttemptQuery["voteAttempt"]
  slug: string
  onRetryVote?: () => void
}
const OptionsList: FC<OptionsListProps> = ({
  governance,
  proposal,
  voteAttempt,
  slug,
  onRetryVote,
}) => {
  const { title } = useDescriptionException({
    governanceId: governance.id,
    proposal,
  })
  const { organization } = useOrganization()
  const { isFeatureFlagOn } = useGovernanceContext()
  const hideCancelProposal = isFeatureFlagOn(FeatureFlag.HideCancelProposal)

  const txHash = getGovernorProposalTxHash(proposal, ProposalEventType.Created)

  invariant(proposal.governor, "governor is required")

  return (
    <Flex>
      <Menu>
        <MenuButton
          as={Button}
          bgColor="white"
          borderColor="gray.300"
          borderWidth="1px"
          px={3}
        >
          <Icon as={EllipsisVertical} fill="gray.600" />
        </MenuButton>
        <MenuList>
          {organization && proposal?.onchainId ? (
            <ProposalShareMenuItem
              url={ROUTES.governance.proposal.id(
                slug,
                proposal.onchainId,
                organization.governorIds.length > 1 ? proposal.governor.id : "",
              )}
            />
          ) : null}

          {organization && title ? (
            <>
              <MenuItem>
                <ShareToFarcasterButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length > 1
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
              <MenuItem>
                <ShareToLensButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length > 1
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
            </>
          ) : null}

          {voteAttempt?.txHash && voteAttempt?.chainId ? (
            <MenuItem>
              <ProposalBlockExplorerURLButton
                chainId={voteAttempt.chainId}
                txHash={voteAttempt.txHash}
              />
            </MenuItem>
          ) : null}

          {!voteAttempt?.txHash && !voteAttempt?.chainId && txHash ? (
            <MenuItem>
              <ProposalBlockExplorerURLButton
                chainId={governance.chainId}
                txHash={txHash}
              />
            </MenuItem>
          ) : null}

          {voteAttempt && onRetryVote ? (
            <MenuItem>
              <Button
                leftIcon={<Icon as={Repeat} />}
                size="sm"
                variant="tertiary"
                width="100%"
                onClick={onRetryVote}
              >
                <Flex width="100%">Retry vote</Flex>
              </Button>
            </MenuItem>
          ) : null}

          {!hideCancelProposal ? (
            <MenuItem>
              <ProposalCancelButton
                governance={governance}
                proposal={proposal}
              />
            </MenuItem>
          ) : null}
        </MenuList>
      </Menu>
    </Flex>
  )
}

export default ProposalActiveCTA
